import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import SidebarLayout from '@/components/layout/SidebarLayout';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { NotFound } from '@/components/shared/NotFound';
import { RedirectToPlayground } from '@/components/shared/RedirectToPlayground';
import { Skeleton } from '@/components/ui/skeleton';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/providers/AuthProvider';
import { ThemeProvider, useTheme } from '@/providers/ThemeProvider';
import api from '@/services/apiClient';

/* ------------------------------------------------------------------ */
/*  Lazy-loaded pages (automatic code splitting)                       */
/* ------------------------------------------------------------------ */

const Dashboard = lazy(() => import('@/pages/Dashboard'));
const AgentBuilder = lazy(() => import('@/pages/AgentBuilder'));
const CallHistory = lazy(() => import('@/pages/CallHistory'));
const AgentList = lazy(() => import('@/pages/AgentList'));
const Playground = lazy(() => import('@/pages/Playground'));
const TwilioAccounts = lazy(() => import('@/pages/TwilioAccounts'));
const Login = lazy(() => import('@/pages/Login'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const Profile = lazy(() => import('@/pages/Profile'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const AdminTenantDetailPage = lazy(() => import('@/pages/admin/AdminTenantDetailPage'));
const PartnersPage = lazy(() => import('@/pages/PartnersPage'));
const AgentLandingVoice = lazy(() => import('@/pages/AgentLandingVoice'));
const ContactsPage = lazy(() => import('@/pages/ContactsPage'));
const ContactDetail = lazy(() => import('@/pages/ContactDetail'));
const CampaignsPage = lazy(() => import('@/pages/CampaignsPage'));
const CampaignDetail = lazy(() => import('@/pages/CampaignDetail'));
const HandoffClaim = lazy(() => import('@/pages/HandoffClaim'));
const KbStatus = lazy(() => import('@/pages/KbStatus'));
const Welcome = lazy(() => import('@/pages/Welcome'));
const SetupPage = lazy(() => import('@/pages/Setup'));
/** Meeting bot page (Phase 1 of MERIDIAN_MEETING_BOT_PLAN.md).
 *  Loaded by Recall's headless Chromium with `?nonce=…`. No auth on
 *  the route — the nonce IS the auth (single-use, 60s TTL). */
const MeetingBot = lazy(() => import('@/pages/MeetingBot'));

/* ------------------------------------------------------------------ */
/*  Public-route helpers                                                */
/* ------------------------------------------------------------------ */

/** Back-compat: any saved deep-link to `/a/:slug/voice` lands here and
 *  redirects to `/a/:slug` (which now serves the pill-then-expand UI). */
function SlugVoiceRedirect(): JSX.Element {
  const { slug = '' } = useParams();
  return <Navigate to={`/a/${encodeURIComponent(slug)}`} replace />;
}

/* ------------------------------------------------------------------ */
/*  Loading fallbacks                                                  */
/* ------------------------------------------------------------------ */

function PageSkeleton() {
  return (
    <div className="p-8 space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-72" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-48 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

function AuthLoadingScreen() {
  const { isDark } = useTheme();
  return (
    <div
      className={`min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-[#0c0c0e]' : 'bg-[#f5f5f3]'}`}
    >
      <img
        src={isDark ? '/otaru-logo-white.png' : '/otaru-logo-color.png'}
        alt="Otaru AI"
        className="w-14 h-14 mb-4"
      />
      <div
        className="h-1 w-32 rounded-full overflow-hidden"
        style={{ background: isDark ? 'rgba(255,255,255,0.06)' : '#dddde0' }}
      >
        <div className="h-full w-1/2 rounded-full bg-gradient-to-r from-blue-500 to-blue-400 animate-pulse" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Route guards                                                       */
/* ------------------------------------------------------------------ */

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RedirectIfAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Probes `/api/admin/me` and renders children only for admins. Non-admins
 * see a clean "Page Not Found" inside the app shell — no admin chrome
 * (header, tabs) renders before the gate resolves, and no 403 error
 * surfaces in the UI. Network/probe failure falls through to children
 * so the page's own error UX (ErrorState card with request id) still
 * works for genuine outages.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<'checking' | 'allowed' | 'denied'>('checking');
  useEffect(() => {
    const ctrl = new AbortController();
    api.admin
      .me(ctrl.signal)
      .then((r) => {
        if (ctrl.signal.aborted) return;
        setStatus(r.isAdmin ? 'allowed' : 'denied');
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        // Network / 5xx — let the inner page render and surface its
        // own error UX rather than masking a real outage as a 404.
        const httpStatus = (err as { status?: number }).status;
        setStatus(httpStatus === 401 || httpStatus === 403 ? 'denied' : 'allowed');
      });
    return () => ctrl.abort();
  }, []);

  if (status === 'checking') return null;
  if (status === 'denied') return <NotFound />;
  return <>{children}</>;
}

/**
 * Catch-all for any URL not matched by the routes above. In a normal
 * browser tab this redirects to "/" (the dashboard for authenticated
 * users, or the login page for guests). When the SPA is loaded INSIDE
 * AN IFRAME (the embed flow), redirecting to "/" would render the
 * dashboard inside a customer's website — a confidential-data leak.
 * The `window.parent !== window` check detects iframe context and
 * renders the sealed fallback instead. Defense-in-depth on top of the
 * `/embed/*` seal: that one only catches embed-prefixed bad paths;
 * this one catches every other unmatched path too (e.g. `/`, `/foo`)
 * so HMR weirdness, browser back/forward, or any future stray
 * navigate-to-root call can never paint dashboard chrome inside an
 * iframe.
 */
function CatchAllRoute() {
  const inIframe = typeof window !== 'undefined' && window.parent !== window;
  if (inIframe) return <EmbedFallback />;
  return <Navigate to="/" replace />;
}

/**
 * Sealed fallback for unknown URLs under /embed/*. Inline-styled (no
 * Tailwind, no providers) so it renders correctly even if global
 * styles haven't loaded inside a sandboxed iframe context. Critically
 * this component MUST NOT redirect or call useNavigate — the entire
 * point is to prevent fall-through to the authenticated app's catch-all.
 */
function EmbedFallback() {
  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '13px',
        color: '#6b7280',
        background: '#ffffff',
        textAlign: 'center',
        padding: '12px',
        boxSizing: 'border-box',
        margin: 0,
      }}
    >
      Voice chat unavailable — please reload.
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  App                                                                */
/* ------------------------------------------------------------------ */

function AppRoutes() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageSkeleton />}>
        <Routes>
          <Route
            path="/login"
            element={
              <RedirectIfAuth>
                <Login />
              </RedirectIfAuth>
            }
          />
          {/* Phase 7: partner slug-page CTA points here. Same form as
              /login (magic link works for both new + existing emails);
              Login.tsx reads `?inherit_agent=<id>` and stashes it for
              the post-verifyOtp clone step in AuthCallback. */}
          <Route
            path="/signup"
            element={
              <RedirectIfAuth>
                <Login />
              </RedirectIfAuth>
            }
          />
          <Route
            element={
              <RequireAuth>
                <SidebarLayout />
              </RequireAuth>
            }
          >
            <Route path="/" element={<Dashboard />} />
            {/* Simplified "Create an agent" flow — the default create door
                and the first-run onboarding target. Renders inside the
                sidebar layout (themed, with nav) — the advanced builder
                lives at /agents/new and is one click away from here. */}
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/agents/new" element={<AgentBuilder />} />
            <Route path="/agents/:id/edit" element={<AgentBuilder />} />
            {/* Legacy test routes — folded into the merged Playground
                (PR-B). RedirectToPlayground preserves the :id param. */}
            <Route path="/agents/:id/voice" element={<RedirectToPlayground />} />
            <Route path="/agents/:id/phone" element={<RedirectToPlayground />} />
            <Route path="/agents/:id/playground" element={<Playground />} />
            <Route path="/agents" element={<AgentList />} />
            <Route path="/calls" element={<CallHistory />} />
            {/* Legacy /trial-leads bookmarks redirect into Call History
                (the old "Trial Leads" view was folded into the unified
                call list and the trial concept itself is now removed). */}
            <Route path="/trial-leads" element={<Navigate to="/calls" replace />} />
            <Route path="/twilio" element={<TwilioAccounts />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminPage />
                </RequireAdmin>
              }
            />
            <Route
              path="/admin/tenants/:id"
              element={
                <RequireAdmin>
                  <AdminTenantDetailPage />
                </RequireAdmin>
              }
            />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/contacts/:id" element={<ContactDetail />} />
            <Route path="/campaigns" element={<CampaignsPage />} />
            <Route path="/campaigns/:id" element={<CampaignDetail />} />
            {/* /dnc was a standalone page; consolidated into Contacts. */}
            <Route path="/dnc" element={<Navigate to="/contacts" replace />} />
            {/* /calls/:id/review (CallReview page) was deleted in
             *  Wave 5b — recordings aren't enabled platform-wide so
             *  the audio player had nothing to play, AND the
             *  transcript field was being read off `metadata.turns`
             *  (stale shape) instead of `metadata.transcript`,
             *  silently rendering empty. The CallHistory slide-out
             *  shows the same transcript correctly. Old links
             *  redirect back to /calls so existing bookmarks /
             *  emails don't 404. */}
            <Route path="/calls/:id/review" element={<Navigate to="/calls" replace />} />
            <Route path="/agents/:id/kb" element={<KbStatus />} />
            <Route path="/profile" element={<Profile />} />
          </Route>
          <Route path="/auth/callback" element={<AuthCallback />} />
          {/* Public slug-share surface. No auth.
              The slug page renders the same pill-then-expand UI as the
              embed iframe, just centered on a full-bleed white page with
              a small "Powered by Otaru AI" footer. The legacy
              `/a/:slug/voice` URL redirects here so old deep links don't
              404. */}
          <Route path="/a/:slug" element={<AgentLandingVoice />} />
          <Route path="/a/:slug/voice" element={<SlugVoiceRedirect />} />
          {/* Embedded iframe surface (Phase E7B-EMBED). Same component
              as the slug landing voice page; mode is inferred from the
              `?key=pk_live_…` query param. Customers paste this URL
              into an <iframe src=...> on their own site. */}
          <Route path="/embed/v1/voice" element={<AgentLandingVoice />} />
          {/* Meeting bot — loaded by Recall's headless Chromium with a
              one-time nonce. No auth. The page exchanges nonce for a
              WS ticket and joins the Gemini Live pipeline. */}
          <Route path="/meeting-bot" element={<MeetingBot />} />
          {/* Sealed embed fallback. CRITICAL: anything under /embed/*
              MUST stay isolated from the authenticated app — without
              this route, an unknown /embed/* URL would fall through to
              the catch-all below and redirect to "/", which is wrapped
              in RequireAuth + SidebarLayout. For a logged-in operator
              testing locally that meant the iframe rendered their
              dashboard inside a customer's website. */}
          <Route path="/embed/*" element={<EmbedFallback />} />
          <Route path="/handoff/:token" element={<HandoffClaim />} />
          <Route path="*" element={<CatchAllRoute />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
